import { endOfDay, format, startOfDay, subDays } from "date-fns";

/** Dashboard filter shape from `Dashboard.tsx` / `DashFilters` (after Apply). */
export type DashboardFiltersState = {
  start_date: string | null;
  end_date: string | null;
  department: (number | string)[];
  dept: (number | string)[];
  category: (number | string)[];
  date_range: string;
  aodm_id: (number | string)[];
};

function formatDateTime(d: Date): string {
  return format(d, "yyyy-MM-dd HH:mm:ss");
}

/**
 * Maps dashboard preset `date_range` (and optional custom start/end) to concrete
 * `start_date` / `end_date` strings so list APIs receive the same window as the dashboard.
 * Aligns with `DashFilters`: today, last_7_days, "" = last 30 days, last_1_year, custom + dates.
 */
export function resolveDashboardDateRangeForList(
  f: DashboardFiltersState | null | undefined
): { start_date?: string; end_date?: string; date_range?: string } {
  if (!f) return {};

  const hasCustom =
    typeof f.start_date === "string" &&
    f.start_date.length > 0 &&
    typeof f.end_date === "string" &&
    f.end_date.length > 0;

  if (hasCustom) {
    return {
      start_date: f.start_date!,
      end_date: f.end_date!,
      ...(f.date_range != null && String(f.date_range).length > 0
        ? { date_range: String(f.date_range) }
        : {}),
    };
  }

  const dr = f.date_range ?? "";
  const now = new Date();

  if (dr === "today") {
    return {
      start_date: formatDateTime(startOfDay(now)),
      end_date: formatDateTime(endOfDay(now)),
      date_range: "today",
    };
  }
  if (dr === "last_7_days") {
    const start = startOfDay(subDays(now, 6));
    const end = endOfDay(now);
    return {
      start_date: formatDateTime(start),
      end_date: formatDateTime(end),
      date_range: "last_7_days",
    };
  }
  if (dr === "" || dr === "last_30_days") {
    const start = startOfDay(subDays(now, 29));
    const end = endOfDay(now);
    return {
      start_date: formatDateTime(start),
      end_date: formatDateTime(end),
      date_range: dr === "" ? "" : "last_30_days",
    };
  }
  if (dr === "last_1_year") {
    const start = startOfDay(subDays(now, 364));
    const end = endOfDay(now);
    return {
      start_date: formatDateTime(start),
      end_date: formatDateTime(end),
      date_range: "last_1_year",
    };
  }
  // If `date_range=custom` is set but we don't have concrete dates,
  // we intentionally return no date params to avoid ambiguous backend filtering.
  if (dr === "custom") return {};
  return {};
}

/**
 * Build list API date params from URL. Prefer explicit `start_date`/`end_date`;
 * otherwise resolve preset `date_range` (including `""` = last 30 days, same as dashboard).
 */
export function resolveDateParamsFromUrlSearch(
  start_date: string | null,
  end_date: string | null,
  date_range: string | null
): { start_date?: string; end_date?: string; date_range?: string } {
  if (start_date && end_date) {
    const out: { start_date?: string; end_date?: string; date_range?: string } = {
      start_date,
      end_date,
    };
    if (date_range != null && date_range !== "")
      out.date_range = date_range;
    return out;
  }
  if (date_range !== null) {
    return resolveDashboardDateRangeForList({
      start_date: null,
      end_date: null,
      department: [],
      dept: [],
      category: [],
      date_range,
      aodm_id: [],
    });
  }
  return {};
}

function appendResolvedDateParams(
  params: URLSearchParams,
  f: DashboardFiltersState | null | undefined
): void {
  if (!f) return;
  const resolved = resolveDashboardDateRangeForList(f);
  if (resolved.start_date) params.set("start_date", resolved.start_date);
  if (resolved.end_date) params.set("end_date", resolved.end_date);
  if (resolved.date_range != null && resolved.date_range !== "")
    params.set("date_range", String(resolved.date_range));
}

/** Observations list: `status` API param uses ids 1=Open, 2=Closed (see `useFilter`). */
export function buildObservationsListUrl(
  dashboardFilters: DashboardFiltersState | null | undefined,
  obsStatus: "all" | "open" | "closed"
): string {
  const params = new URLSearchParams();
  if (obsStatus === "open") params.set("status", "1");
  else if (obsStatus === "closed") params.set("status", "2");

  const f = dashboardFilters;
  if (f) {
    if (f.department?.length)
      params.set("departments", f.department.map(String).join(","));
    if (f.category?.length)
      params.set("categories", f.category.map(String).join(","));
    if (f.aodm_id?.length) params.set("users", f.aodm_id.map(String).join(","));
    appendResolvedDateParams(params, f);
  }
  const qs = params.toString();
  return qs ? `/observations?${qs}` : "/observations";
}

export function buildSnagsListUrl(
  dashboardFilters: DashboardFiltersState | null | undefined,
  snagStatus: "all" | "pending" | "closed" | "parked" | "rejected"
): string {
  const params = new URLSearchParams();
  if (snagStatus !== "all") {
    const map = {
      pending: "Pending",
      closed: "Closed",
      parked: "Parked",
      rejected: "Rejected",
    } as const;
    params.set("request_statuses", map[snagStatus]);
  }
  const f = dashboardFilters;
  if (f) {
    if (f.dept?.length) params.set("dept_ids", f.dept.map(String).join(","));
    if (f.aodm_id?.length)
      params.set("user_id", f.aodm_id.map(String).join(","));
    appendResolvedDateParams(params, f);
  }
  const qs = params.toString();
  return qs ? `/snags?${qs}` : "/snags";
}
